import React from 'react';
import { PlayerResult } from './GameResult';
import { t } from 'i18next';
import { getScoreClass } from '@/utils/scoreUtils';

interface ScorecardViewProps {
  player: PlayerResult;
}

const Scorecard: React.FC<ScorecardViewProps> = ({ player }) => {
  return (
    <div className="bg-card rounded-lg shadow-sm overflow-hidden mb-4 relative">
      {/* <div className="absolute inset-0 opacity-15 z-0 rounded-2xl overflow-hidden">
        <img
          src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/image-0dmZmLVz1ed3gcS8oBw3DAvSJd7VCN.png"
          alt="Golf green background"
          className="w-full h-full object-cover"
        />
      </div> */}
      <div className="relative z-10">
        <div className="p-2">
          <div className="grid md:grid-cols-12 grid-cols-11 text-xs font-bold text-center text-secondary-foreground bg-secondary rounded-lg py-2 mb-3">
            <div className="text-left pl-3 min-w-12 col-span-2">{t('hole')}</div>
            <div>1</div>
            <div>2</div>
            <div>3</div>
            <div>4</div>
            <div>5</div>
            <div>6</div>
            <div>7</div>
            <div>8</div>
            <div>9</div>
            <div className="hidden md:block">{t('golf_front')}</div>
          </div>

          <div className="grid md:grid-cols-12 grid-cols-11 text-xs text-center mb-2">
            <div className="text-gray-500 pl-3 py-1 text-left col-span-2 truncate">{t('golf_handicap')}</div>
            {player.scores.slice(0, 9).map((score) => (
              <div key={`hcp-front-${score.hole}`} className="py-1 text-muted-foreground">
                {/* Determine handicap based on hole number - common pattern where odd-numbered holes are harder */}
                {score.hole % 2 === 1 ? score.hole : 18 - score.hole}
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-12 grid-cols-11 text-xs text-center mb-3">
            <div className="text-gray-500 text-left pl-3 py-1 col-span-2">Par</div>
            {player.scores.slice(0, 9).map((score) => (
              <div key={`par-front-${score.hole}`} className="py-1">
                {score.par}
              </div>
            ))}
            <div className="hidden md:block py-1">{player.scores.slice(0, 9).reduce((sum, score) => sum + (score.par || 0), 0)}</div>
          </div>

          <div className="grid md:grid-cols-12 grid-cols-11 text-xs text-center mb-3">
            <div className="text-gray-500 text-left pl-3 py-1 col-span-2">Score</div>
            {player.scores.slice(0, 9).map((score) => (
              <div
                key={`score-front-${score.hole}`}
                className={`py-1 ${getScoreClass(score.score, score.par)} border-none`}
              >
                {score.score === 0 ? '-' : score.score}
              </div>
            ))}
            <div className="hidden md:block py-1">{player.scores.slice(0, 9).reduce((sum, score) => sum + (score.score || 0), 0)}</div>
          </div>

          <div className="grid grid-cols-11 md:grid-cols-12 gap-1 text-xs text-center font-bold text-secondary-foreground bg-secondary rounded-lg py-2 mb-3">
            <div className="text-left pl-3 min-w-12 col-span-2">{t('hole')}</div>
            <div>10</div>
            <div>11</div>
            <div>12</div>
            <div>13</div>
            <div>14</div>
            <div>15</div>
            <div>16</div>
            <div>17</div>
            <div>18</div>
            <div className="hidden md:block">{t('golf_back')}</div>
          </div>
          <div className="grid grid-cols-11 md:grid-cols-12 gap-1 text-xs text-center mb-2">
            <div className="text-gray-500 col-span-2 pl-3 py-1 text-left truncate">{t('golf_handicap')}</div>
            {player.scores.slice(9, 18).map((score) => (
              <div key={`hcp-back-${score.hole}`} className="py-1 text-muted-foreground">
                {/* Determine handicap based on hole number - common pattern where odd-numbered holes are harder */}
                {score.hole % 2 === 0 ? score.hole - 9 : 27 - score.hole}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-11 md:grid-cols-12 gap-1 text-xs text-center mb-3">
            <div className="text-gray-500 col-span-2 pl-3 py-1 text-left">Par</div>
            {player.scores.slice(9, 18).map((score) => (
              <div key={`par-back-${score.hole}`} className="py-1">
                {score.par}
              </div>
            ))}
            <div className="hidden md:block py-1">{player.scores.slice(9, 18).reduce((sum, score) => sum + (score.par || 0), 0)}</div>
          </div>
          <div className="grid grid-cols-11 md:grid-cols-12 gap-1 text-xs text-center font-bold mb-4">
            <div className="text-gray-500 col-span-2 pl-3 py-1 text-left">Score</div>
            {player.scores.slice(9, 18).map((score) => (
              <div key={`score-back-${score.hole}`} className={`py-1 ${getScoreClass(score.score, score.par)} border-none`}>
                {score.score === 0 ? '-' : score.score}
              </div>
            ))}
            <div className="hidden md:block py-1">{player.scores.slice(9, 18).reduce((sum, score) => sum + (score.score || 0), 0)}</div>
          </div>

          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-secondary rounded-xl p-3">
              <div className="text-xs text-gray-500">{t('golf_front')}</div>
              <div className="text-lg font-semibold">
                {player.scores.slice(0, 9).reduce((sum, hole) => sum + (hole.score || 0), 0)}
              </div>
            </div>
            <div className="bg-secondary rounded-xl p-3">
              <div className="text-xs text-gray-500">{t('golf_back')}</div>
              <div className="text-lg font-semibold">
                {player.scores.slice(9, 18).reduce((sum, hole) => sum + (hole.score || 0), 0)}
              </div>
            </div>
            <div className="bg-secondary rounded-xl p-3">
              <div className="text-xs text-primary">Total</div>
              <div className="text-lg font-bold text-primary">{player.totalScore}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scorecard;
